import { acquisitionAtMonth } from '@/domain/acquisition';
import { headcountAtMonth } from '@/domain/headcount';
import type { SimulatorState } from '@/domain/types';

export type MonthlySnapshot = {
  month: number;
  clientsPerTier: Record<string, number>;
  totalClients: number;
  mrrPerTier: Record<string, number>;
  mrr: number;
  arr: number;
  /** Revenu ponctuel encaissé ce mois-ci via les frais d'entrée des nouveaux clients (hors MRR). */
  oneTimeRevenueThisMonth: number;
  /** Coût ponctuel (COGS) supporté ce mois-ci sur les ventes d'entrée des nouveaux clients. */
  oneTimeCostThisMonth: number;
  /** Coût mensuel **récurrent** (ETP + coûts récurrents + support par client +
   *  avant-vente), propre au mois observé. Varia mois par mois lorsque la montée
   *  en charge de l'effectif est activée. */
  monthlyCost: number;
  /** Signatures de deals ce mois-ci (mode discret uniquement, sinon 0). La facturation
   *  démarre `salesCycleMonths` plus tard. */
  signaturesThisMonth: number;
  /** Clients activés (première facturation) ce mois-ci en mode discret, sinon 0. */
  newActivationsThisMonth: number;
  /** Coût d'avant-vente du mois : dossiers traités × coût par dossier (mode discret, sinon 0). */
  presalesCostThisMonth: number;
  /** Coût de support variable du mois : Σ clients actifs × coût par client du palier. */
  variableCostThisMonth: number;
  /** Composante "équipe" du `monthlyCost` ce mois-ci (avant ajout des coûts récurrents). */
  teamCostThisMonth: number;
  /** Effectif (ETP, fractions) retenu pour le calcul du `teamCostThisMonth`. */
  headcountThisMonth: number;
  fixedCostThisMonth: number;
  margin: number;
  cumulativeRevenue: number;
  cumulativeCost: number;
  cashFlow: number;
};

export type ProjectionResult = {
  snapshots: MonthlySnapshot[];
  operationalBreakEvenMonth: number | null;
  globalBreakEvenMonth: number | null;
  finalKpis: {
    mrr: number;
    arr: number;
    totalClients: number;
  };
};

/** Absorbe la dérive flottante des accumulateurs avant `Math.floor`
 *  (0.1 accumulé 10 fois ne vaut pas exactement 1.0). */
const EMISSION_EPSILON = 1e-9;

/**
 * Pré-calcule, en mode discret, les signatures **entières** émises par palier et par mois.
 * Le rythme attendu (`acquisitionAtMonth`, courbe constant/linear-ramp/s-curve inchangée)
 * alimente un accumulateur par palier ; un deal est signé chaque fois que l'accumulateur
 * franchit 1. Déterministe : le cumul émis vaut toujours `floor(cumul attendu + ε)`.
 */
function planDiscreteSignatures(state: SimulatorState): {
  signaturesPerTierByMonth: number[][];
  totalSignaturesByMonth: number[];
} {
  const { tiers, acquisition, projection } = state;
  const signaturesPerTierByMonth: number[][] = tiers.map(() => []);
  const totalSignaturesByMonth: number[] = [];
  const accumulators: number[] = tiers.map(() => 0);

  for (let month = 1; month <= projection.horizonMonths; month += 1) {
    const expectedSignatures = acquisitionAtMonth(month, acquisition);
    let totalThisMonth = 0;
    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i];
      if (!tier) continue;
      const accumulated = (accumulators[i] ?? 0) + expectedSignatures * tier.acquisitionShare;
      const emitted = Math.floor(accumulated + EMISSION_EPSILON);
      accumulators[i] = accumulated - emitted;
      signaturesPerTierByMonth[i]?.push(emitted);
      totalThisMonth += emitted;
    }
    totalSignaturesByMonth.push(totalThisMonth);
  }

  return { signaturesPerTierByMonth, totalSignaturesByMonth };
}

/**
 * Coût mensuel récurrent **hors équipe** : somme des `recurringCosts` (constant sur l'horizon).
 * L'éventuelle variabilité mensuelle du coût total vient de la composante équipe,
 * calculée mois par mois dans la boucle de `computeProjection`.
 */
function computeMonthlyRecurringCost(state: SimulatorState): number {
  return state.recurringCosts.reduce((sum, cost) => sum + cost.monthlyAmount, 0);
}

/**
 * Coût mensuel de l'équipe au mois donné :
 *   `headcount(month) × avgAnnualCostPerFTE × chargesMultiplier / 12`
 * Reflète la montée en charge de l'effectif lorsqu'elle est activée, sinon reste
 * constant (= coût mensuel total historique).
 */
function computeTeamCostForMonth(
  state: SimulatorState,
  month: number,
): {
  cost: number;
  headcount: number;
} {
  const { team } = state;
  const headcount = headcountAtMonth(month, state);
  const cost = (headcount * team.avgAnnualCostPerFTE * (team.chargesMultiplier ?? 1)) / 12;
  return { cost, headcount };
}

/**
 * Mois d'atteinte du break-even global (revenu cumulé ≥ coût cumulé) en projetant
 * *au-delà* de l'horizon configuré, jusqu'à `maxMonths`. Sert à indiquer, quand le
 * break-even n'est pas atteint sur la période visualisée, la durée qui serait
 * néanmoins nécessaire — ou `null` s'il n'est jamais atteint à ce rythme (le
 * plateau de marge mensuelle est ≤ 0, ou l'atteinte dépasse `maxMonths`).
 *
 * Les coûts fixes ponctuels sont contraints par le schéma à `occursAtMonth ≤
 * horizonMonths` : prolonger l'horizon n'en introduit donc aucun nouveau, et le
 * résultat coïncide avec `computeProjection` pour les mois ≤ horizon.
 */
export function computeGlobalBreakEvenMonth(state: SimulatorState, maxMonths = 600): number | null {
  if (state.projection.horizonMonths >= maxMonths) {
    return computeProjection(state).globalBreakEvenMonth;
  }
  const extended = computeProjection({
    ...state,
    projection: { ...state.projection, horizonMonths: maxMonths },
  });
  return extended.globalBreakEvenMonth;
}

// Borne supérieure de recherche : si même 1 M clients/mois ne suffit pas, ROI infaisable.
const ROI_MAX_SEARCH = 1_000_000;

/**
 * Rythme de croisière minimum (`acquisition.newClientsPerMonth`) permettant
 * d'atteindre le break-even global *dans l'horizon* configuré, ou `null` si c'est
 * infaisable même au plafond de recherche.
 *
 * Seul le rythme de croisière varie : le modèle d'acquisition et sa durée de
 * montée en charge sont conservés, sans quoi le résultat serait incohérent avec le
 * break-even effectivement affiché (une montée lente en courbe en S exige un
 * plateau plus élevé qu'un modèle constant pour un même horizon).
 *
 * Limitation : la recherche binaire suppose que « break-even atteint » est monotone
 * en fonction du rythme. Si l'économie unitaire d'un palier est négative
 * (`costPerClientPerMonth ≥ monthlyPrice`, ou avant-vente supérieure à la marge
 * dégagée dans l'horizon), accélérer peut aggraver les pertes et la monotonie se
 * brise — limitation acceptée.
 */
export function requiredCruisingSpeedForRoi(state: SimulatorState): number | null {
  const reached = (newClientsPerMonth: number): boolean =>
    computeProjection({
      ...state,
      acquisition: { ...state.acquisition, newClientsPerMonth },
    }).globalBreakEvenMonth !== null;

  if (!reached(ROI_MAX_SEARCH)) return null;

  // Recherche binaire du plus petit rythme atteignant le break-even dans l'horizon.
  let lo = 0;
  let hi = ROI_MAX_SEARCH;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (reached(mid)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return hi;
}

export function computeProjection(state: SimulatorState): ProjectionResult {
  const { tiers, fixedCosts, acquisition, projection } = state;
  const monthlyRecurringCost = computeMonthlyRecurringCost(state);

  // Mode discret (vente B2G) : signatures entières pré-calculées, facturation décalée
  // de `salesCycleMonths`, churn entier par accumulateur, coût d'avant-vente par dossier.
  // Toutes les structures sont locales : la fonction reste pure et déterministe.
  const discrete = acquisition.discrete === true;
  const salesCycleMonths = acquisition.salesCycleMonths ?? 0;
  const winRate = acquisition.winRate ?? 1;
  const presalesCostPerDeal = acquisition.presalesCostPerDeal ?? 0;
  const discretePlan = discrete ? planDiscreteSignatures(state) : null;
  const churnAccumulators: Record<string, number> = {};

  const clientsPerTier: Record<string, number> = {};
  for (const tier of tiers) {
    clientsPerTier[tier.id] = 0;
    churnAccumulators[tier.id] = 0;
  }

  const snapshots: MonthlySnapshot[] = [];
  let cumulativeRevenue = 0;
  // Le déficit initial (trésorerie déjà consommée avant le mois 1) amorce le coût cumulé :
  // le cash flow démarre à -initialDeficit et doit être remboursé avant le break-even global.
  let cumulativeCost = state.initialDeficit ?? 0;
  let operationalBreakEvenMonth: number | null = null;
  let globalBreakEvenMonth: number | null = null;
  let finalMrr = 0;
  let finalArr = 0;
  let finalTotalClients = 0;

  for (let month = 1; month <= projection.horizonMonths; month += 1) {
    const mrrPerTier: Record<string, number> = {};
    let mrr = 0;
    let totalClients = 0;
    let oneTimeRevenueThisMonth = 0;
    let oneTimeCostThisMonth = 0;
    let newActivationsThisMonth = 0;
    let variableCostThisMonth = 0;

    const newClientsThisMonth = acquisitionAtMonth(month, acquisition);

    for (let tierIndex = 0; tierIndex < tiers.length; tierIndex += 1) {
      const tier = tiers[tierIndex];
      if (!tier) continue;
      const previousClients = clientsPerTier[tier.id] ?? 0;

      let currentClients: number;
      let newClientsForTier: number;
      if (discretePlan) {
        // Facturation décalée : les signatures du mois `month - salesCycleMonths`
        // deviennent des clients actifs ce mois-ci (index négatif → aucune activation).
        const activations =
          discretePlan.signaturesPerTierByMonth[tierIndex]?.[month - 1 - salesCycleMonths] ?? 0;
        // Churn entier : un client ne part que lorsque l'accumulateur franchit 1.
        const churnAccumulated =
          (churnAccumulators[tier.id] ?? 0) + previousClients * tier.monthlyChurnRate;
        const departures = Math.min(
          Math.floor(churnAccumulated + EMISSION_EPSILON),
          previousClients,
        );
        churnAccumulators[tier.id] = churnAccumulated - departures;
        currentClients = previousClients - departures + activations;
        newClientsForTier = activations;
        newActivationsThisMonth += activations;
      } else {
        newClientsForTier = newClientsThisMonth * tier.acquisitionShare;
        currentClients = previousClients * (1 - tier.monthlyChurnRate) + newClientsForTier;
      }
      clientsPerTier[tier.id] = currentClients;
      totalClients += currentClients;

      const tierMrr = currentClients * tier.monthlyPrice;
      mrrPerTier[tier.id] = tierMrr;
      mrr += tierMrr;

      // Frais d'entrée : encaissés une seule fois, à l'acquisition des nouveaux clients du
      // palier (à l'activation en mode discret), nets du coût unitaire (COGS) supporté sur
      // chaque vente d'entrée.
      oneTimeRevenueThisMonth += newClientsForTier * (tier.entryPrice ?? 0);
      oneTimeCostThisMonth += newClientsForTier * (tier.entryCost ?? 0);

      variableCostThisMonth += currentClients * (tier.costPerClientPerMonth ?? 0);
    }

    const arr = mrr * 12;
    const fixedCostThisMonth = fixedCosts
      .filter((cost) => cost.occursAtMonth === month)
      .reduce((sum, cost) => sum + cost.amount, 0);
    const { cost: teamCostThisMonth, headcount: headcountThisMonth } = computeTeamCostForMonth(
      state,
      month,
    );
    // Avant-vente : chaque signature du mois représente `1 / winRate` dossiers traités
    // (les dossiers perdus coûtent aussi), facturés `presalesCostPerDeal` chacun.
    const signaturesThisMonth = discretePlan?.totalSignaturesByMonth[month - 1] ?? 0;
    const presalesCostThisMonth = (signaturesThisMonth / winRate) * presalesCostPerDeal;
    const monthlyCost =
      teamCostThisMonth + monthlyRecurringCost + variableCostThisMonth + presalesCostThisMonth;
    const margin = mrr - monthlyCost;

    cumulativeRevenue += mrr + oneTimeRevenueThisMonth;
    cumulativeCost += monthlyCost + fixedCostThisMonth + oneTimeCostThisMonth;
    const cashFlow = cumulativeRevenue - cumulativeCost;

    if (operationalBreakEvenMonth === null && mrr >= monthlyCost) {
      operationalBreakEvenMonth = month;
    }
    if (globalBreakEvenMonth === null && cumulativeRevenue >= cumulativeCost) {
      globalBreakEvenMonth = month;
    }

    snapshots.push({
      month,
      clientsPerTier: { ...clientsPerTier },
      totalClients,
      mrrPerTier,
      mrr,
      arr,
      oneTimeRevenueThisMonth,
      oneTimeCostThisMonth,
      monthlyCost,
      signaturesThisMonth,
      newActivationsThisMonth,
      presalesCostThisMonth,
      variableCostThisMonth,
      teamCostThisMonth,
      headcountThisMonth,
      fixedCostThisMonth,
      margin,
      cumulativeRevenue,
      cumulativeCost,
      cashFlow,
    });

    finalMrr = mrr;
    finalArr = arr;
    finalTotalClients = totalClients;
  }

  return {
    snapshots,
    operationalBreakEvenMonth,
    globalBreakEvenMonth,
    finalKpis: {
      mrr: finalMrr,
      arr: finalArr,
      totalClients: finalTotalClients,
    },
  };
}
